/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { css, cx } from "@panda/css";
import { CosmosTitle, CosmosTitleProps } from "./CosmosTitle.molecule";
import { EdsIcon, CosmosLink } from "@components/elements";
import { CosmosEnumEdsGradients } from "@cosmos/hygraph-schema";
import { useMemo } from "react";

export interface LinkTitleProps extends CosmosTitleProps {
	gradient?: CosmosEnumEdsGradients | null;
}

export const extractHrefFromHtml = (html: string): string | null => {
	const match = html.match(/<a[^>]*href=["']([^"']+)["'][^>]*>/i);
	return match ? match[1] : null;
};

export const removeAnchorTags = (html: string): string => {
	return html.replace(/<a[^>]*>/gi, "").replace(/<\/a>/gi, "");
};

const styles = css({
	"& .link-title-chevron": {
		display: "inline-flex",
		alignItems: "center",
		"& svg": {
			transform: "translateX(0px)",
			transition: "transform token(animations.eds.duration.150) token(animations.eds.easing.easeInOutQuad)",
		},
	},
	_hover: {
		"& .link-title-chevron svg": {
			transform: "translateX(8px)",
		},
	},
});

const linkStyles = css({
	display: "inline-flex",
	alignItems: "center",
	gap: "eds.dimension.12",
	textDecoration: "none",
});

export const LinkTitle = (props: LinkTitleProps): React.JSX.Element => {
	const { className, title } = props;

	// Memoize based on title.html to avoid recalculations when title object reference changes
	const linkHref = useMemo(() => (title?.html ? extractHrefFromHtml(title.html) : null), [title?.html]);

	const cleanHtml = useMemo(() => (title?.html ? removeAnchorTags(title.html) : undefined), [title?.html]);

	const cleanTitle = title?.html
		? {
				...title,
				html: cleanHtml,
				text: title.text || cleanHtml?.replace(/<[^>]*>/g, ""),
			}
		: title;

	if (!linkHref) {
		if (process.env.NODE_ENV === "development") {
			console.warn("LinkTitle: Failed to extract valid href from HTML. Falling back to ColorfulTitle.", {
				html: title?.html,
			});
		}
		return <CosmosTitle {...props} />;
	}

	return (
		<CosmosLink href={linkHref} className={cx("link-title", styles, linkStyles, className)}>
			<CosmosTitle {...props} title={cleanTitle} />
			<span className="link-title-chevron">
				<EdsIcon icon="ChevronRight" size="lg" color="primary" />
			</span>
		</CosmosLink>
	);
};
